import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ShieldAlert,
  Wifi,
  Clock,
  XCircle,
  Info,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { LoginHeader } from "@/components/auth/LoginHeader";
import { LoginForm } from "@/components/auth/LoginForm";
import { useAuth } from "@/contexts/AuthContext";
import { getLocalizedPublicPath } from '../../lib/publicLocaleRouting'

interface ErrorDetails {
  type: "error" | "warning" | "info";
  icon: React.ElementType;
  title: string;
  message: string;
  suggestion?: string;
}

const LOGIN_SLIDES = [
  {
    textKey: "auth.login.brand.text1",
    imageUrl:
      "https://images.unsplash.com/photo-1630165356623-266076eaceb6?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
  {
    textKey: "auth.login.brand.text2",
    imageUrl:
      "https://images.unsplash.com/photo-1566274360936-69fae8dc1d95?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
  {
    textKey: "auth.login.brand.text3",
    imageUrl:
      "https://images.unsplash.com/photo-1558981403-c5f9899a28bc?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
  {
    textKey: "auth.login.brand.text4",
    imageUrl:
      "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
  {
    textKey: "auth.login.brand.text5",
    imageUrl:
      "https://plus.unsplash.com/premium_photo-1664302152991-d013ff125f3f?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
] as const;

const FEATURE_KEYS = [
  "auth.login.brand.feature1",
  "auth.login.brand.feature2",
  "auth.login.brand.feature3",
] as const;

export const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [textVisible, setTextVisible] = useState(true);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const successMessage =
    location.state?.successMessage || location.state?.message;

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email)
    }
  }, [location.state])

  useEffect(() => {
    let fadeTimeout: ReturnType<typeof setTimeout>
    const interval = setInterval(() => {
      setTextVisible(false)
      fadeTimeout = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % LOGIN_SLIDES.length)
        setTextVisible(true)
      }, 350)
    }, 4500)
    return () => {
      clearInterval(interval)
      clearTimeout(fadeTimeout)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorDetails(null);
    setIsLoading(true);

    try {
      await login(email, password);
      navigate("/");
    } catch (err: unknown) {
      let errorInfo: ErrorDetails = {
        type: "error",
        icon: XCircle,
        title: t("auth.login.errors.generic"),
        message: t("auth.login.errors.genericMsg"),
        suggestion: t("auth.login.errors.genericSuggestion"),
      };

      let errorMsg = "";
      if (err instanceof Error) {
        errorMsg = err.message.toLowerCase();
      } else if (err !== null && typeof err === "object" && "message" in err) {
        errorMsg = String(
          (err as Record<string, unknown>).message
        ).toLowerCase();
      } else if (typeof err === "string") {
        errorMsg = err.toLowerCase();
      }

      if (!errorMsg) errorMsg = "unknown";

      if (
        errorMsg.includes('not verified') ||
        errorMsg.includes('email not verified') ||
        errorMsg.includes('email não verificado') ||
        errorMsg.includes('email nao verificado') ||
        errorMsg.includes('não verificado') ||
        errorMsg.includes('nao verificado')
      ) {
        const verificationPath = getLocalizedPublicPath(
          location.pathname,
          '/verify-email',
          i18n.resolvedLanguage || i18n.language,
        )
        const verificationQuery = new URLSearchParams()
        if (email.trim()) verificationQuery.set('email', email.trim())

        navigate(
          `${verificationPath}${verificationQuery.toString() ? `?${verificationQuery.toString()}` : ''}`,
          {
            replace: true,
            state: {
              email: email.trim(),
              message: t('auth.verifyEmail.loginRequiredMessage'),
            },
          },
        )
        return
      }

      if (
        errorMsg.includes("401") ||
        errorMsg.includes("unauthorized") ||
        errorMsg.includes("credenciais") ||
        errorMsg.includes("inválid") ||
        errorMsg.includes("invalido") ||
        errorMsg.includes("incorret") ||
        errorMsg.includes("senha")
      ) {
        errorInfo = {
          type: "error",
          icon: ShieldAlert,
          title: t("auth.login.errors.invalidCredentials"),
          message: t("auth.login.errors.invalidCredentialsMsg"),
          suggestion: t("auth.login.errors.invalidCredentialsSuggestion"),
        };
      } else if (
        errorMsg.includes("403") ||
        errorMsg.includes("forbidden") ||
        errorMsg.includes("bloqueado")
      ) {
        errorInfo = {
          type: "warning",
          icon: AlertTriangle,
          title: t("auth.login.errors.accessBlocked"),
          message: t("auth.login.errors.accessBlockedMsg"),
          suggestion: t("auth.login.errors.accessBlockedSuggestion"),
        };
      } else if (
        errorMsg.includes("network") ||
        errorMsg.includes("fetch") ||
        errorMsg.includes("conexão")
      ) {
        errorInfo = {
          type: "warning",
          icon: Wifi,
          title: t("auth.login.errors.connectionError"),
          message: t("auth.login.errors.connectionErrorMsg"),
          suggestion: t("auth.login.errors.connectionErrorSuggestion"),
        };
      } else if (
        errorMsg.includes("timeout") ||
        errorMsg.includes("tempo")
      ) {
        errorInfo = {
          type: "warning",
          icon: Clock,
          title: t("auth.login.errors.timeout"),
          message: t("auth.login.errors.timeoutMsg"),
          suggestion: t("auth.login.errors.timeoutSuggestion"),
        };
      } else if (
        errorMsg.includes("500") ||
        errorMsg.includes("502") ||
        errorMsg.includes("503") ||
        errorMsg.includes("504")
      ) {
        errorInfo = {
          type: "error",
          icon: AlertTriangle,
          title: t("auth.login.errors.serverError"),
          message: t("auth.login.errors.serverErrorMsg"),
          suggestion: t("auth.login.errors.serverErrorSuggestion"),
        };
      } else if (
        errorMsg.includes("429") ||
        errorMsg.includes("muitas tentativas")
      ) {
        errorInfo = {
          type: "warning",
          icon: Clock,
          title: t("auth.login.errors.tooManyAttempts"),
          message: t("auth.login.errors.tooManyAttemptsMsg"),
          suggestion: t("auth.login.errors.tooManyAttemptsSuggestion"),
        };
      } else if (
        err instanceof Error &&
        err.message &&
        !err.message.includes("Error")
      ) {
        errorInfo = {
          type: "error",
          icon: Info,
          title: t("auth.login.errors.generic"),
          message: err.message,
          suggestion: t("auth.login.errors.genericSuggestion"),
        };
      }

      setErrorDetails(errorInfo);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (errorDetails) setErrorDetails(null);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (errorDetails) setErrorDetails(null);
  };

  const handleForgotPassword = () => {
    setErrorDetails({
      type: "info",
      icon: Info,
      title: t("auth.login.forgotPasswordInfo"),
      message: t("auth.login.forgotPasswordInfoMsg"),
      suggestion: t("auth.login.forgotPasswordInfoSuggestion"),
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* ═══ LEFT BRAND PANEL ═══ */}
      <div
        className={cn(
          "hidden lg:flex lg:w-1/2 xl:w-[58%]",
          "relative flex-col justify-between p-12 overflow-hidden",
          "bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600",
          "animate-in fade-in slide-in-from-left-8 duration-700"
        )}
      >
        {LOGIN_SLIDES.map((slide, index) => (
          <div
            key={slide.imageUrl}
            aria-hidden
            className={cn(
              "absolute inset-0 bg-cover bg-center transition-all duration-700 ease-out",
              index === currentTextIndex && textVisible
                ? "opacity-100 translate-x-0 scale-100"
                : "opacity-0 translate-x-6 scale-[1.02]"
            )}
            style={{ backgroundImage: `url("${slide.imageUrl}")` }}
          />
        ))}
        <div
          aria-hidden
          className="absolute inset-0 bg-primary/75 pointer-events-none"
        />

        {/* Decorative glow orb — gold top-right */}
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "hsl(var(--secondary) / 0.14)" }}
        />
        {/* Decorative glow orb — cyan bottom-left */}
        <div
          aria-hidden
          className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "hsl(var(--accent) / 0.07)" }}
        />
        {/* Subtle grid pattern overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Logo */}
        <div className="relative animate-in fade-in zoom-in-95 duration-1000">
          <img
            src="/logo.svg"
            alt="ambcar"
            className="h-9 w-auto brightness-0 invert"
          />
        </div>

        {/* Central motivational content */}
        <div className="relative space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-200">
          <div className="space-y-5">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-secondary">
              {t("auth.login.brand.headline")}
            </p>
            {/* Rotating text */}
            <div
              className="h-24 flex items-start"
              aria-live="polite"
              aria-atomic="true"
            >
              <blockquote
                className={cn(
                  "text-2xl xl:text-[1.75rem] font-bold text-primary-foreground leading-snug",
                  "transition-opacity duration-300 ease-in-out",
                  textVisible ? "opacity-100" : "opacity-0"
                )}
              >
                &ldquo;{t(LOGIN_SLIDES[currentTextIndex].textKey)}&rdquo;
              </blockquote>
            </div>
          </div>

          {/* Feature list */}
          <ul className="space-y-4">
            {FEATURE_KEYS.map((key) => (
              <li key={key} className="flex items-center gap-3">
                <CheckCircle2
                  className="h-5 w-5 flex-shrink-0 text-secondary"
                  strokeWidth={1.5}
                />
                <span className="text-sm font-medium text-primary-100">
                  {t(key)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom copyright */}
        <div className="relative animate-in fade-in duration-700 delay-500">
          <p className="text-xs text-primary-300">
            &copy; {new Date().getFullYear()} ambcar &middot;{" "}
            {t("auth.login.subtitle")}
          </p>
        </div>
      </div>

      {/* ═══ RIGHT FORM PANEL ═══ */}
      <div
        className={cn(
          "w-full lg:w-1/2 xl:w-[42%]",
          "flex items-center justify-center",
          "bg-background",
          "px-6 py-12 lg:px-16",
          "animate-in fade-in slide-in-from-right-8 duration-700"
        )}
      >
        <div className="w-full max-w-md space-y-8">
          <LoginHeader />

          <div className="space-y-5">
            {successMessage && (
              <ErrorAlert type="success" message={successMessage} />
            )}

            {errorDetails && (
              <ErrorAlert
                type={errorDetails.type}
                title={errorDetails.title}
                message={errorDetails.message}
                suggestion={errorDetails.suggestion}
                icon={errorDetails.icon}
                onClose={() => setErrorDetails(null)}
              />
            )}

            <LoginForm
              email={email}
              password={password}
              isLoading={isLoading}
              onEmailChange={handleEmailChange}
              onPasswordChange={handlePasswordChange}
              onSubmit={handleSubmit}
              onForgotPassword={handleForgotPassword}
            />
          </div>

          {/* Footer links */}
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-500">
            <p className="text-center text-sm text-muted-foreground">
              {t("auth.login.noAccount")}{" "}
              <button
                onClick={() => navigate(getLocalizedPublicPath(location.pathname, "/plans", i18n.resolvedLanguage || i18n.language))}
                className="font-semibold text-accent underline underline-offset-4 hover:opacity-80 transition-opacity"
              >
                {t("auth.login.registerLink")}
              </button>
            </p>

            <div className="border-t border-border pt-4">
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.login.needHelp")}{" "}
                <a
                  href="#"
                  className="font-semibold text-accent underline underline-offset-4 hover:opacity-80 transition-opacity"
                >
                  {t("auth.login.contactUs")}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
